// UserMenu for the tenant portal topbar.
//
// Per tenant-shell-strings.md §"UserMenu". Mirrors the admin shape:
// trigger = name + role caption + avatar; dropdown = header + items +
// danger sign-out. Items: Profile, Preferences, Sign out. (No
// "shortcuts" — that's admin-only.) Consumer sign-out opens a confirm
// dialog (calmer than admin's direct redirect).

import { useEffect, useRef, useState } from 'react';
import { Avatar, ChevronDownIcon } from '@gc-coach/design-system';
import { useAuth, useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { SignOutConfirm } from './SignOutConfirm.js';
import styles from './UserMenu.module.css';

export function UserMenu() {
  const { t } = useI18n();
  const { status, signOut } = useAuth();
  const { session, isLoading } = useSession();

  const [open, setOpen] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (status.kind !== 'authenticated') return null;

  if (!session) {
    return (
      <div className={styles.wrapper}>
        <button
          type="button"
          className={styles.trigger}
          aria-busy={isLoading}
          disabled
        >
          <span className={styles.triggerText}>
            <span className={styles.triggerName}>{t('shell.userMenuLoading')}</span>
          </span>
        </button>
      </div>
    );
  }

  const { displayName, email } = session;
  // V1 consumer = always tenant_admin. Role caption in the trigger reads
  // "TENANT ADMIN" — when tenant_member ships (V1.x), this swaps to that.
  const roleCaption = t('shell.userMenuRoleTenantAdmin');

  function onSignOutClick() {
    setOpen(false);
    setConfirmingSignOut(true);
  }
  async function onSignOutConfirmed() {
    setConfirmingSignOut(false);
    await signOut();
  }

  return (
    <>
      <div className={styles.wrapper} ref={wrapperRef}>
        <button
          type="button"
          className={[styles.trigger, open ? styles.triggerOpen : null]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className={styles.triggerText}>
            <span className={styles.triggerName}>{displayName}</span>
            <span className={styles.triggerRole}>{roleCaption}</span>
          </span>
          <Avatar name={displayName} email={email} size="sm" />
          <ChevronDownIcon
            width={10}
            height={10}
            className={[styles.chevron, open ? styles.chevronOpen : null]
              .filter(Boolean)
              .join(' ')}
          />
        </button>

        {open && (
          <div className={styles.dropdown} role="menu">
            <header className={styles.dropdownHeader}>
              <Avatar name={displayName} email={email} size="lg" />
              <div className={styles.dropdownHeaderText}>
                <span className={styles.dropdownName}>{displayName}</span>
                <span className={styles.dropdownEmail}>{email}</span>
              </div>
            </header>

            <ul className={styles.dropdownList} role="none">
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className={styles.dropdownItem}
                  disabled
                >
                  {t('shell.userMenuProfile')}
                </button>
              </li>
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className={styles.dropdownItem}
                  disabled
                >
                  {t('shell.userMenuPreferences')}
                </button>
              </li>
            </ul>

            <div className={styles.dropdownDivider} />
            <button
              type="button"
              role="menuitem"
              className={styles.dropdownDanger}
              onClick={onSignOutClick}
            >
              {t('shell.userMenuSignOut')}
            </button>
          </div>
        )}
      </div>

      {confirmingSignOut && (
        <SignOutConfirm
          onCancel={() => setConfirmingSignOut(false)}
          onConfirm={onSignOutConfirmed}
        />
      )}
    </>
  );
}
